'use client'

// Note: it's important for these to be the same version
// so the @datadog/browser-core package is a shared dependency who's side effects only run once
import { datadogLogs, type LogsEvent } from '@datadog/browser-logs'
import {
  datadogRum,
  type RumErrorEvent,
  type RumEvent,
} from '@datadog/browser-rum'
import { setRouteTracer } from '@polaris/core/machines'
import { useAtomValue } from 'jotai'
import { useEffect, useRef } from 'react'
import type { DatadogConfig } from '../configs/datadog'
import { sessionIdAtom, userAtom } from '../stores/user/atoms'
import { createDatadogRouteTracer } from '../utils/privacy-route-tracing'

/**
 * Initializes DataDog RUM from RSC props.
 *
 * https://docs.datadoghq.com/real_user_monitoring/browser/setup/
 */
export function DataDog({ config }: { config: DatadogConfig }) {
  const isInitialized = useRef(false)
  const guestOrUser = useAtomValue(userAtom)
  const sessionId = useAtomValue(sessionIdAtom)

  useEffect(() => {
    if (isInitialized.current) return

    const user =
      guestOrUser.id !== 'guest'
        ? guestOrUser
        : sessionId
          ? { id: sessionId }
          : undefined

    initDatadogRum(config)
    initDatadogLogs(config)

    // Initialize privacy route tracing after RUM is ready
    // This enables timing/measurement of route phases for optimization dashboards
    setRouteTracer(createDatadogRouteTracer())

    if (user) {
      datadogRum.setUser(user)
      datadogLogs.setUser(user)
    }

    isInitialized.current = true
  }, [config, guestOrUser, sessionId])

  return null
}

// Note: the Script deferred import strategy has already been attempted with "https://www.datadoghq-browser-agent.com/"
// however, it was not working, likely due to ad blockers. This approach is less
// detectable by ad blockers, and has been confirmed to work.

function initDatadogRum(config: DatadogConfig) {
  if (!config.enabled) {
    console.warn('Datadog RUM is not enabled. Skipping initialization.')
    return
  }
  if (!config.applicationId) {
    console.error(
      'Datadog RUM is not initialized. Missing process.env.DATADOG_APPLICATION_ID.',
    )
    return
  }

  if (!config.clientToken) {
    console.error(
      'Datadog RUM is not initialized. Missing process.env.DATADOG_CLIENT_TOKEN.',
    )
    return
  }

  if (datadogRum.getInternalContext()) {
    return
  }

  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: config.site,
    service: config.service,
    env: config.env,
    version: config.version,
    proxy: config.proxy,

    trackingConsent: config.trackingConsent,

    sessionSampleRate: config.sessionSampleRate
      ? Number.parseInt(config.sessionSampleRate)
      : 5,
    sessionReplaySampleRate: config.sessionReplaySampleRate
      ? Number.parseInt(config.sessionReplaySampleRate)
      : 1,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',

    // Connects RUM + Tracing
    // See also:
    // https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-nextjs-app-with-rum/?tab=npm
    // https://docs.datadoghq.com/real_user_monitoring/platform/connect_rum_and_traces/?tab=browserrum
    allowedTracingUrls: [
      {
        match: /https:\/\/.*\.polaris\.app/,
        propagatorTypes: ['datadog', 'tracecontext'],
      },
    ],
    traceSampleRate: 100, // Default is 100, just want to be explicit
    traceContextInjection: 'all', // Default is all, just want to be explicit
    beforeSend: (log) => {
      if (discardLogsEvent(log)) {
        return false
      }

      return true
    },
  })
}

function initDatadogLogs(config: DatadogConfig) {
  if (!config.enabled) {
    console.warn('Datadog Logs is not enabled. Skipping initialization.')
    return
  }

  if (!config.clientToken) {
    console.error(
      'Datadog RUM is not initialized. Missing process.env.DATADOG_CLIENT_TOKEN.',
    )
    return
  }

  if (datadogLogs.getInternalContext()) {
    return
  }

  datadogLogs.init({
    clientToken: config.clientToken,
    site: config.site,
    service: config.service,
    env: config.env,
    proxy: config.proxy,
    trackingConsent: config.trackingConsent,

    // Note: logs are much cheaper so we can afford to sample 100% of sessions
    sessionSampleRate: 100,

    ...(process.env.NODE_ENV === 'development'
      ? {}
      : {
          forwardErrorsToLogs: true,
          forwardConsoleLogs: 'all',
        }),

    beforeSend: (log) => {
      if (discardLogsEvent(log)) {
        return false
      }

      log.message = `[web] ${log.message}`
      return true
    },
  })

  datadogLogs.setGlobalContext({
    version: config.version,
    session_id: datadogRum.getInternalContext()?.session_id,
  })
}

/**
 * Hook to manage Datadog custom attributes lifecycle.
 * Sets the provided attributes as global context properties and cleans them up on unmount.
 */
export function useDatadogCustomAttributes(
  customAttributes?: Record<string, string>,
) {
  useEffect(() => {
    if (!customAttributes) return

    // Using `setGlobalContextProperty` instead of `setGlobalContext` allows us to set
    // properties on global context without overwriting existing properties.
    // This increases flexibility as we set different properties across various app routes.
    for (const key in customAttributes) {
      datadogRum.setGlobalContextProperty(key, customAttributes[key])
      datadogLogs.setGlobalContextProperty(key, customAttributes[key])
    }

    return () => {
      for (const key in customAttributes) {
        datadogRum.removeGlobalContextProperty(key)
        datadogLogs.removeGlobalContextProperty(key)
      }
    }
  }, [customAttributes])
}

function discardLogsEvent(logsEvent: LogsEvent | RumEvent) {
  // AbortSignals are used in app to cancel requests we don't need anymore.
  // We shouldn't consider these intential events as errors.
  if (
    logsEvent.status === 'error' &&
    logsEvent.error instanceof Error &&
    (logsEvent.error.stack?.includes('AbortError') ||
      logsEvent.error.name.includes('AbortError') ||
      ('cause' in logsEvent.error &&
        logsEvent.error.cause instanceof Error &&
        logsEvent.error.cause.name.includes('AbortError')) ||
      logsEvent.error.message.includes('signal is aborted without reason') ||
      logsEvent.error.message.includes('AbortError'))
  ) {
    return true
  }

  const error = logsEvent.error as RumErrorEvent['error']

  if (error?.kind) {
    const rejectedRequest =
      error.causes?.find(
        (cause) =>
          cause.message.toLowerCase().includes('request rejected') ||
          cause.message.toLowerCase().includes('user rejected'),
      ) !== undefined ||
      error.stack?.toLowerCase().includes('request rejected') ||
      error.stack?.toLowerCase().includes('user rejected')

    if (rejectedRequest) {
      return true
    }
  }

  // Injected wallet or lib warnings
  if (
    typeof logsEvent.message === 'string' &&
    (logsEvent.message.includes('stargate') ||
      logsEvent.message.includes('no-legacy-stdTx'))
  ) {
    return true
  }

  return false
}
